import { PARAMETER_EMBEDDING_TARGET_NAME } from '@disy/lib.backend/api-constants';
import { cadenzaUrl } from '@disy/lib.backend/cadenza-url';
import { requireNonNullable } from '@disy/lib/custom-error';
import { logger } from '@disy/lib/logging';
import { extendUrlParams, getUrlParams } from '@disy/lib/url-param-util';
import { closeMenu } from '@disy/ui/menu';
import { getSetting } from '@disy/workbook.lib/settings';

import { createWorkbookUrl } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import { theme } from 'cadenza/theming-api';
import { navigateWithinWorkbook } from 'cadenza/utils/confirm-navigation-util';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key.gen';
import { duplicateWorksheet } from 'cadenza/workbook/workbook-core-actions.state';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getWorkbookNameAndHash,
  getWorkbookRepositoryName,
  getWorksheetById,
  getWorksheets,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

/**
 * Get the color of the worksheet, if available. (Without "auto"-calculation {@link getWorksheetBackgroundColor}.)
 *
 * @param {Worksheet} worksheet - the worksheet to get the color from
 * @return {string|null|undefined} - a hex color string if the worksheet it is available
 */
function getWorksheetBackgroundColorInternal(worksheet: WorksheetDto) {
  return getSetting(worksheet, WorksheetSettingsKey.toPersistenceKey('WORKSHEET_BACKGROUND_COLOR'));
}

/**
 * Get whether the color of the worksheet is set in auto-mode.
 *
 * @param {Worksheet} worksheet - the worksheet to get the color from
 * @return {boolean} - whether the color is set in auto-mode
 */
export function getWorksheetBackgroundColorAuto(worksheet: WorksheetDto) {
  return getWorksheetBackgroundColorInternal(worksheet) == null;
}

function getWorksheetViewBorders(worksheet: WorksheetDto) {
  return getSetting<boolean>(worksheet, WorksheetSettingsKey.toPersistenceKey('WORKSHEET_VIEW_BORDERS'));
}

export function getWorksheetViewBordersAuto(worksheet: WorksheetDto) {
  return getWorksheetViewBorders(worksheet) == null;
}

export function calculateWorksheetViewBorders(id: WorksheetId, state: RootStateOr<WorkbookState>) {
  const viewBorders = getWorksheetViewBorders(getWorksheetById(state, id));
  if (viewBorders != null) {
    return viewBorders;
  }
  const worksheets = getWorksheets(state);
  const worksheetIndex = worksheets.map((worksheet) => worksheet.id).indexOf(id);
  const previousWorksheets = worksheets.slice(0, worksheetIndex);
  while (previousWorksheets.length > 0) {
    const previousWorksheet = requireNonNullable(previousWorksheets.pop());
    const borders = getWorksheetViewBorders(previousWorksheet);
    if (borders != null) {
      return borders;
    }
  }
  return theme('workbook-view-border');
}

/**
 * Get the value of the visible setting of the worksheet.
 *
 * @param worksheet - The worksheet to get the setting from.
 * @return {boolean} - True if the worksheet is visible, default: true.
 */
export function isWorksheetVisible(worksheet: WorksheetDto) {
  return getSetting(worksheet, WorksheetSettingsKey.toPersistenceKey('WORKSHEET_VISIBLE'), true);
}

export function addWorksheetClickHandler(worksheets: WorksheetDto[], { duplicate }: { duplicate?: boolean } = {}) {
  closeMenu();
  import('cadenza/workbook/add-worksheet-dialog/add-worksheet-dialog')
    .then(({ AddWorksheetDialog }) => {
      const dialog = new AddWorksheetDialog(worksheets, { duplicate });
      dialog.addEventListener('close', () => dialog.close());
      document.body.append(dialog);
      return dialog.connectAndShow();
    })
    .catch((error) => logger.error('failed to create AddWorksheetDialog', error));
}

export function openImportWorksheetDialog() {
  import('cadenza/workbook/import-worksheet-dialog/import-worksheet-dialog')
    .then(async ({ ImportWorksheetDialog }) => {
      const result = await new ImportWorksheetDialog().connectAndShow();
      if (result) {
        const {
          worksheet: { repositoryName, workbookName, worksheetId },
          printName,
        } = result;
        const workbookId = { itemType: 'WORKBOOK' as const, repositoryName, itemName: workbookName };
        const createdWorksheetId = await duplicateWorksheet(printName, worksheetId, workbookId);
        navigateWithinWorkbook(
          cadenzaUrl(
            createWorkbookUrl(
              getWorkbookRepositoryName(getStore().getState()),
              getWorkbookNameAndHash(getStore().getState()),
              {
                worksheetId: createdWorksheetId,
              },
            ),
          ),
        );
      }
    })
    .catch((error) => logger.error('failed to create ImportWorksheetDialog', error));
}

export async function deleteWorksheet(worksheetId: WorksheetId, callback?: () => void) {
  const command = Command.REMOVE_WORKSHEET({ worksheetId });
  try {
    await getStore().dispatch(executeCommand(command));
    if (callback) {
      callback();
    }
  } catch (error) {
    logger.error('Error deleting worksheet: ', error);
  }
}

export function getWorksheetCadenzaUrl(
  repositoryName: RepositoryName,
  worksheetId: WorksheetId,
  workbookNameAndHash: WorkbookNameAndHash,
) {
  let url = cadenzaUrl(
    createWorkbookUrl(repositoryName, workbookNameAndHash, {
      worksheetId,
    }),
  );
  const urlParameters = getUrlParams(location.href);

  // Keep embedding target context when navigating between worksheets.
  if (urlParameters[PARAMETER_EMBEDDING_TARGET_NAME]) {
    url = extendUrlParams(url, urlParameters);
  }
  return url;
}
